import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "src/components/Dialog";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import {
  XGrid,
  GridSelectionModel,
  type GridColumns,
} from "@material-ui/x-grid";

import { setActiveDialog } from "src/store/adminHtml/actions";
import { buildZendeskLink } from "src/store/adminHtml/selector";
import {
  isNewSyncInventoryDownloadsLoading,
  getNewSyncInventoryDownloadsCompareData,
  getNewSyncInventoryDownloadsErrors,
  getNewSyncInventoryDownloadsWithData,
} from "src/store/uploads/selector";
import { getNewInventoryDownloads } from "src/store/uploads/actions";
import valueGetter from "src/utils/valueGetter";
import {
  userIdSelector,
  selectUser,
  featureFlagSelector,
} from "src/store/system/selector";
import { openZendeskWithSSO } from "src/utils/zendeskSso";

import {
  InventorySyncDialogContent,
  isDataRowRemoveItemFromImport,
  type DataRow,
} from "src/pages/RootDialogs/InventorySyncDialog";
import DialogTitle from "src/components/DialogTitleWithClose";
import WalkThrough from "./WalkThrough";
import InventoryImportWalkThrough from "./InventoryImportWalkThrough";
// Previous pending UI: PendingInventoryReview (InventorySyncDialog table).
// Swap this import back to that file to fall back while the new flow is tested.
import PendingInventoryProcess from "src/pages/RootDialogs/PendingInventoryProcess";
import DialogContentText from "@material-ui/core/DialogContentText";

// fix-vim-highligh = }

const useInventorySyncDialogStyles = makeStyles({
  paper: {
    height: "calc(100% - 64px)",
    maxHeight: "calc(100% - 64px)",
    display: "flex",
    flexDirection: "column",
    "& > .MuiDialogTitle-root, & > .MuiDialogActions-root": {
      flexShrink: 0,
    },
  },
});

const ColumnsTitle: GridColumns = [
  {
    valueGetter,
    field: "current.item_title",
    headerName: "Item Title",
    flex: 1,
  },
  {
    valueGetter,
    field: "current.list_date",
    headerName: "Duplicate List Date",
    description:
      "This is the list date of the item currently in inventory that matches the title of a new eBay listing",
    type: "date",
    width: 150,
  },
  {
    valueGetter,
    field: "new.list_date",
    headerName: "New Item List Date",
    description:
      "This is the list date of the new listing that we received from eBay",
    type: "date",
    width: 150,
  },
  {
    valueGetter,
    field: "current.quantity",
    headerName: "Current Item Count",
    description:
      "This is the total number of items with this item title in your current inventory",
    width: 150,
  },
  {
    valueGetter,
    field: "new.quantity",
    headerName: "New Item Count",
    description:
      "This is the number of items being brought in with the same item title as an item in the inventory",
    width: 150,
  },
];
const ColumnSkuCurrentListDateDescription =
  "This is the list date of the item currently in inventory that matches the SKU of a new eBay listing.";
const ColumnSkuCurrentItemCount =
  "This is the total number of items with this sku in your current inventory.";
const ColumnSkuNewItemCount =
  "This is the number of items being brought in with the same sku as an item in your inventory.";
const ColumnsSku: GridColumns = [
  {
    valueGetter,
    field: "current.item_title",
    headerName: "Current Item Title",
    flex: 1,
  },
  {
    valueGetter,
    field: "new.item_title",
    headerName: "New Item Title",
    flex: 1,
  },
  ...ColumnsTitle.reduce((cs, c) => {
    if (c.field !== "current.item_title") {
      if (c.field === "current.list_date") {
        c = {
          ...c,
          description: ColumnSkuCurrentListDateDescription,
        };
      } else if (c.field === "current.quantity") {
        c = {
          ...c,
          description: ColumnSkuCurrentItemCount,
        };
      } else if (c.field === "new.quantity") {
        c = {
          ...c,
          description: ColumnSkuNewItemCount,
        };
      }
      cs.push(c);
    }
    return cs;
  }, [] as GridColumns),
];

const useStyles = makeStyles((theme) => ({
  content: {
    height: "80vh",
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

function DuplicateInventoryCheckerContent({
  onClose,
  initialReviewAll = true,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [reviewAll, setReviewAll] = useState(initialReviewAll);
  const { matchesTitle, matchesSku, not } = useSelector((s) =>
    getNewSyncInventoryDownloadsCompareData(s, reviewAll)
  );
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const isLoading = useSelector(isNewSyncInventoryDownloadsLoading);
  const downloadErrors = useSelector(getNewSyncInventoryDownloadsErrors);
  const downloadsWithData = useSelector(getNewSyncInventoryDownloadsWithData);
  const userId = useSelector(userIdSelector);
  const user = useSelector(selectUser);
  const [actions, setActions] = useState<Record<string, DataRow>>({});
  const { pendingMatchesTitle, pendingMatchesSku } = useMemo(() => {
    return {
      pendingMatchesTitle: matchesTitle.filter(({ id }) => !actions[id]),
      pendingMatchesSku: matchesSku.filter(({ id }) => !actions[id]),
    };
  }, [matchesTitle, matchesSku, actions]);

  useEffect(() => {
    dispatch(getNewInventoryDownloads());
  }, [dispatch]);

  const pendingMatches = pendingMatchesTitle.length
    ? pendingMatchesTitle
    : pendingMatchesSku;
  const matches = pendingMatchesTitle.length ? matchesTitle : matchesSku;
  const Columns = pendingMatchesTitle.length ? ColumnsTitle : ColumnsSku;

  const loadFailed =
    !isLoading &&
    downloadErrors.length > 0 &&
    downloadsWithData.length === 0 &&
    matchesTitle.length === 0 &&
    matchesSku.length === 0 &&
    not.length === 0;

  const syncDialogOpen =
    pendingMatchesTitle.length === 0 &&
    pendingMatchesSku.length === 0 &&
    !isLoading &&
    !loadFailed;
  const data = useMemo(() => {
    if (!syncDialogOpen) return [];
    return [...Object.values(actions), ...not].map((action) => {
      if (!isDataRowRemoveItemFromImport(action))
        return {
          ...action,
          purchase_price: 0,
        };
      return action;
    });
  }, [syncDialogOpen, actions, not]);

  const downloadTimestamp = reviewAll
    ? undefined
    : (not[0] as any)?.download_timestamp ||
      (matchesTitle[0] as any)?.new?.download_timestamp ||
      (matchesSku[0] as any)?.new?.download_timestamp;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (isLoading || reviewAll || loadFailed) return;
    if (
      matchesTitle.length === 0 &&
      matchesSku.length === 0 &&
      not.length === 0
    )
      onCloseRef.current({});
  }, [isLoading, reviewAll, loadFailed, matchesTitle, matchesSku, not]);

  if (syncDialogOpen) {
    const switchDisabled = matchesTitle.length > 0 || matchesSku.length > 0;
    return (
      <>
        <InventoryImportWalkThrough />
        <InventorySyncDialogContent
          downloadTimestamp={downloadTimestamp}
          title={
            <DialogTitle
              onClose={onClose}
              extra={
                <FormControlLabel
                  id="inventory-import-review-all-switch"
                  label="Review All"
                  disabled={switchDisabled}
                  control={
                    <Switch
                      color="primary"
                      disabled={switchDisabled}
                      checked={reviewAll}
                      onChange={(e) => {
                        setReviewAll(e.target.checked);
                      }}
                    />
                  }
                />
              }
            >
              Inventory Import
            </DialogTitle>
          }
          data={data}
          onClose={() => {
            onClose({}, !reviewAll ? "partialReview" : undefined);
          }}
        />
      </>
    );
  }

  if (loadFailed) {
    const openSupportRequest = () => {
      if (!user) return;
      const downloadDetails = downloadErrors
        .map(
          (d) =>
            `- id=${d.id}; type=${d.type}; filename=${d.filename}; timestamp=${
              d.timestamp
            }; error=${d.error || "unknown"}`
        )
        .join("\n");
      openZendeskWithSSO(
        buildZendeskLink(
          user,
          "Pending inventory load error",
          [
            "Hello, I received an error loading pending inventory downloads. Can you look into this for me?",
            "",
            "Download details:",
            downloadDetails,
          ].join("\n"),
          downloadErrors.map((d) => d.id).join(",") ||
            "inventory-download-load-error"
        )
      );
    };

    return (
      <>
        <DialogTitle onClose={onClose}>Pending Inventory</DialogTitle>
        <DialogContent className={classes.content}>
          <DialogContentText>
            We couldn’t load your pending inventory
            {downloadErrors.length > 1
              ? ` (${downloadErrors.length} files)`
              : ""}
            . Please try again. If the problem continues, contact support so we
            can help resolve it.
          </DialogContentText>
          {downloadErrors.some((d) => d.error) && (
            <DialogContentText component="div">
              <ul>
                {downloadErrors
                  .filter((d) => d.error)
                  .map((d) => (
                    <li key={d.id}>
                      {d.filename}: {d.error}
                    </li>
                  ))}
              </ul>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              dispatch(getNewInventoryDownloads());
            }}
          >
            Retry
          </Button>
          <Button
            color="primary"
            variant="outlined"
            disabled={!user}
            onClick={openSupportRequest}
          >
            Contact Support
          </Button>
          <Button color="secondary" variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <WalkThrough loading={isLoading} />
      <DialogTitle
        onClose={onClose}
        extra={
          <FormControlLabel
            id="duplicate-inventory-checker-review-all-switch"
            label="Review All"
            control={
              <Switch
                color="primary"
                checked={reviewAll}
                onChange={(e) => {
                  setReviewAll(e.target.checked);
                  setSelectionModel([]);
                }}
              />
            }
          />
        }
      >
        Duplicate Inventory Checker{" "}
        {pendingMatchesTitle.length ? "(Title Matches)" : "(SKU Matches)"}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <XGrid
          className="duplicate-inventory-checker-x-grid"
          selectionModel={selectionModel}
          onSelectionModelChange={(sm) => setSelectionModel(sm)}
          checkboxSelection
          disableSelectionOnClick
          columns={Columns}
          rows={pendingMatches}
          loading={isLoading}
          density="compact"
        />
      </DialogContent>
      <DialogActions>
        <div
          id="duplicate-inventory-checker-actions"
          className="MuiDialogActions-spacing"
        >
          <Button
            className={classes.deleteButton}
            color="secondary"
            variant="contained"
            disabled={isLoading || selectionModel.length === 0}
            onClick={() => {
              if (!selectionModel.length) return;
              const selected = selectionModel.reduce((a, id) => {
                const matched = matches.find((m) => m.id === id);
                if (matched) {
                  a[id] = {
                    remove: true,
                    inventoryId: matched.current.id,
                    listingId: matched.new.listing_id,
                  };
                }
                return a;
              }, {});
              setActions((a) => ({ ...a, ...selected }));
              setSelectionModel([]);
            }}
          >
            Remove from Import
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              const selected = selectionModel.reduce((a, v) => {
                const ele = matches.find(({ id }) => id === v);
                if (ele) {
                  a[v] = { user: userId, ...ele.new };
                }
                return a;
              }, {});
              setActions((a) => ({ ...a, ...selected }));
              setSelectionModel([]);
            }}
            disabled={isLoading || selectionModel.length === 0}
          >
            Add to Import
          </Button>
        </div>
      </DialogActions>
    </>
  );
}

export interface DuplicateInventoryCheckerProps {
  open: boolean;
}

function DuplicateInventoryChecker({ open }: DuplicateInventoryCheckerProps) {
  const classes = useInventorySyncDialogStyles();
  const dispatch = useDispatch();
  const pendingInventoryEnabled = useSelector((s) =>
    featureFlagSelector(s, "pendingInventory")
  );
  const [key, setKey] = useState(0);
  const handleClose = (e?, reason?: string) => {
    if (reason) {
      if (reason === "partialReview") setKey((k) => k + 1);
      return;
    }
    dispatch(setActiveDialog(""));
    setKey(0);
  };

  return (
    <Dialog
      key={key}
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="xl"
      disableEscapeKeyDown
      classes={{ paper: classes.paper }}
    >
      {open &&
        (pendingInventoryEnabled ? (
          <PendingInventoryProcess onClose={handleClose} />
        ) : (
          <DuplicateInventoryCheckerContent
            onClose={handleClose}
            initialReviewAll={key === 0}
          />
        ))}
    </Dialog>
  );
}

export default DuplicateInventoryChecker;
